import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {NormalMapSockets} from "#material/nodes/vector/normal-map/definition"
import {normalMapDefinition} from "#material/nodes/vector/normal-map/definition"
import {asVec3, threeConvert, threeRGBColor, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import type {NormalMapNode} from "#material/cycles/types"
import {cyclesColor} from "#material/cycles/helpers"
import {cyclesSpace} from "#material/nodes/vector/normal-map/helpers"

@registerMaterialGraphNode
export class NormalMap extends MaterialNode<NormalMapSockets> {
    public override get definition() {
        return normalMapDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<NormalMapSockets>> => {
        const {color, strength} = await evaluator.getAll({
            color: this.parameters.inputs.color,
            strength: this.parameters.inputs.strength,
        })

        const colorValue = color?.toColor() ?? threeConvert(this.parameters.settings.color, threeRGBColor) ?? threeRGBColor({r: 0.5, g: 0.5, b: 1.0})
        const strengthValue = strength?.toFloat() ?? threeConvert(this.parameters.settings.strength, threeValue) ?? threeValue(1)

        return {normal: asVec3(TSL.normalMap(colorValue, TSL.vec2(strengthValue, strengthValue)))}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<NormalMapSockets>> => {
        const normalMapNode: NormalMapNode = {
            type: "normal_map",
            inputs: await evaluator.getAll({color: this.parameters.inputs.color, strength: this.parameters.inputs.strength}),
            parameters: {
                attribute: this.parameters.settings.uvMapIndex !== undefined ? `UVMap${this.parameters.settings.uvMapIndex}` : undefined,
                strength: this.parameters.settings.strength,
                color: cyclesColor(this.parameters.settings.color),
                space: cyclesSpace(this.parameters.settings.space),
            },
            resources: {},
        }

        return {normal: [normalMapNode, "normal"]}
    }
}
